import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { ShieldCheck, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import Logo from "@/components/Logo";
import AuthThreadMini from "@/components/auth/AuthThreadMini";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ez = mmEase.expo;
const OTP_LENGTH = 6;

export default function TwoFactor() {
  const location        = useLocation();
  const navigate        = useNavigate();
  const { refresh }     = useAuth();
  const emailFromState  = location.state?.email || "";

  const [digits,       setDigits      ] = useState(Array(OTP_LENGTH).fill(""));
  const [loading,      setLoading     ] = useState(false);
  const [error,        setError       ] = useState(null);
  const [showBackup,   setShowBackup  ] = useState(false);
  const [backupCode,   setBackupCode  ] = useState("");
  const [backupLoading,setBackupLoading] = useState(false);
  const [backupError,  setBackupError ] = useState(null);
  const inputRefs     = useRef([]);
  const backupInputRef = useRef(null);

  // Auto-focus first slot on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  function handleDigitChange(idx, value) {
    const char = value.replace(/\D/g, "").slice(-1);
    const next  = digits.map((d, i) => (i === idx ? char : d));
    setDigits(next);
    if (char && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
    // Auto-submit when all filled
    if (char && next.every(Boolean)) {
      submitCode(next.join(""));
    }
  }

  function handleKeyDown(idx, e) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("").map((_, i) => pasted[i] || "");
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
    if (pasted.length === OTP_LENGTH) submitCode(pasted);
  }

  async function submitCode(code) {
    setLoading(true); setError(null);
    try {
      await api.post("/auth/2fa/verify", { code, email: emailFromState });
      await refresh();
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect code. Please try again.");
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    const code = digits.join("");
    if (code.length === OTP_LENGTH) submitCode(code);
  }

  useEffect(() => {
    if (showBackup) setTimeout(() => backupInputRef.current?.focus(), 50);
  }, [showBackup]);

  async function submitBackupCode(e) {
    e?.preventDefault();
    const code = backupCode.replace(/\s/g, "").toUpperCase();
    if (!code) return;
    setBackupLoading(true);
    setBackupError(null);
    try {
      await api.post("/auth/2fa/backup", { code, email: emailFromState });
      await refresh();
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setBackupError(err?.response?.data?.detail || "Invalid backup code. Please check and try again.");
      setBackupCode("");
      backupInputRef.current?.focus();
    } finally {
      setBackupLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left — brand panel ───────────────────────────────── */}
      <div className="hidden lg:flex w-[440px] xl:w-[500px] flex-col hero-gradient grain relative overflow-hidden">
        <div
          className="absolute top-[22%] left-[10%] w-64 h-64 rounded-full blur-3xl animate-orb pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[15%] right-[8%] w-48 h-48 rounded-full blur-3xl animate-orb-b pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.14) 0%, transparent 70%)" }}
        />

        <div className="relative z-10 p-10 flex flex-col h-full">
          <Link to="/"><Logo variant="dark" size="md" /></Link>

          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: ez }}
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-4">
                Two-factor authentication
              </p>
              <h1
                className="font-display font-black text-white leading-[1.08] text-balance mb-5"
                style={{ fontSize: "clamp(1.7rem, 2.8vw, 2.4rem)", letterSpacing: "-0.04em" }}
              >
                One more step to keep you safe.
              </h1>
              <p className="text-white/48 text-[14px] leading-relaxed max-w-sm">
                We take security seriously. This extra step ensures only you can access your money.
              </p>
            </motion.div>

            <motion.div
              className="mt-12 space-y-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.28, ease: ez }}
            >
              {[
                { title: "Open your authenticator app", body: "Google Authenticator, Authy, or 1Password." },
                { title: "Find MoneyMeet Pay", body: "Look for the 6-digit code next to the MoneyMeet entry." },
                { title: "Enter the code", body: "Codes expire every 30 seconds — enter it quickly." },
              ].map(({ title, body }, i) => (
                <div key={title} className="flex gap-3">
                  <div
                    className="w-6 h-6 rounded-full grid place-items-center shrink-0 text-[10px] font-bold text-white mt-0.5"
                    style={{ background: "rgba(124,58,237,0.30)", border: "1px solid rgba(124,58,237,0.45)" }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-white/85">{title}</p>
                    <p className="text-[12px] text-white/45 mt-0.5">{body}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Thread */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45, ease: ez }}
            >
              <AuthThreadMini color="#7C3AED" />
            </motion.div>
          </div>

          <div className="flex items-center gap-5 mb-5">
            <div className="flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-emerald" strokeWidth={2.5} />
              <span className="text-[10px] font-semibold text-white/38">TOTP — RFC 6238</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Lock size={12} className="text-emerald" strokeWidth={2.5} />
              <span className="text-[10px] font-semibold text-white/38">Bank-grade Encryption</span>
            </div>
          </div>

          <p className="text-white/22 text-[11px]">© {new Date().getFullYear()} MoneyMeet Pay</p>
        </div>
      </div>

      {/* ── Right — OTP form ──────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center bg-white px-5 py-12">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden mb-8">
            <Link to="/"><Logo variant="light" size="md" /></Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, ease: ez }}
          >
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-navy transition-colors mb-8"
            >
              <ArrowLeft size={13} /> Back to sign in
            </Link>

            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2">
              Security verification
            </p>
            <h2
              className="font-display font-black text-navy tracking-tight mb-1"
              style={{ fontSize: "1.65rem", letterSpacing: "-0.035em" }}
            >
              Enter your code
            </h2>
            <p className="text-sm text-muted-foreground mb-10">
              {emailFromState
                ? `Authenticating ${emailFromState}`
                : "Enter the 6-digit code from your authenticator app."}
            </p>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.44, ease: ez, delay: 0.08 }}
            >
              {/* OTP digit inputs */}
              <div
                className="flex items-center justify-center gap-2.5 mb-6"
                onPaste={handlePaste}
                data-testid="2fa-otp-group"
                role="group"
                aria-label="One-time code"
              >
                {digits.map((d, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { inputRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    data-testid={`2fa-digit-${idx}`}
                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    disabled={loading}
                    className="w-12 h-14 text-center rounded-xl text-navy font-mono font-bold text-xl outline-none transition-all disabled:opacity-50"
                    style={{
                      border: d ? "2px solid #10B981" : "1.5px solid rgba(11,29,58,0.14)",
                      boxShadow: d ? "0 0 0 3px rgba(16,185,129,0.14)" : "none",
                      background: d ? "rgba(16,185,129,0.04)" : "white",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                    aria-label={`Digit ${idx + 1}`}
                  />
                ))}
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5 text-center"
                  role="alert"
                >
                  {error}
                </motion.div>
              )}

              <button
                type="submit"
                data-testid="2fa-submit-button"
                disabled={loading || digits.some((d) => !d)}
                className="btn-primary w-full justify-center py-3.5 text-sm disabled:opacity-55 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                    Verifying…
                  </span>
                ) : (
                  <>Verify code <ArrowRight size={14} /></>
                )}
              </button>
            </motion.div>
          </form>

          {!showBackup ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, ease: ez, delay: 0.22 }}
              className="mt-8 text-center"
            >
              <p className="text-[12px] text-muted-foreground mb-2">
                Can't access your authenticator?
              </p>
              <button
                type="button"
                data-testid="2fa-backup-link"
                className="text-[12px] font-semibold text-emerald hover:text-emerald-hover transition-colors"
                onClick={() => { setShowBackup(true); setError(null); }}
              >
                Use a backup code →
              </button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: ez }}
              className="mt-8"
            >
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3 text-center">
                Backup code
              </p>
              <form onSubmit={submitBackupCode} className="space-y-3">
                <input
                  ref={backupInputRef}
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  autoComplete="one-time-code"
                  data-testid="2fa-backup-input"
                  className="input-field w-full text-center font-mono tracking-[0.3em] uppercase"
                  aria-label="Backup code"
                />
                {backupError && (
                  <p className="text-sm text-red-600 text-center" role="alert">{backupError}</p>
                )}
                <button
                  type="submit"
                  disabled={backupLoading || !backupCode.trim()}
                  data-testid="2fa-backup-submit"
                  className="btn-primary w-full justify-center py-3 text-sm disabled:opacity-55 disabled:cursor-not-allowed"
                >
                  {backupLoading ? (
                    <span className="flex items-center gap-2 justify-center">
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Verifying…
                    </span>
                  ) : (
                    <>Verify backup code <ArrowRight size={14} /></>
                  )}
                </button>
              </form>
              <button
                type="button"
                onClick={() => { setShowBackup(false); setBackupCode(""); setBackupError(null); }}
                className="mt-3 w-full text-center text-[12px] text-muted-foreground hover:text-navy transition-colors"
              >
                ← Back to authenticator code
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
